import React, { useState, useEffect } from 'react';
import { ref, onValue } from "firebase/database";
import { database } from "../constants/firebase";
import BunnyVideoEmbed from "./BunnyVideoEmbed";
import { FiMessageSquare } from "react-icons/fi";

const Testimonials = ({ productId, isCreator }) => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);

    useEffect(() => {
        if (!productId) return;

        const testimonialsRef = ref(database, `Listings/${productId}/testimonials`);
        const unsubscribe = onValue(testimonialsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
                
                // For regular users, only show featured
                // For creators, show all (or they manage via the modal)
                // Actually, this component is for public display, so only show featured.
                const filtered = list.filter(item => item.featured);
                setTestimonials(filtered.sort((a, b) => b.createdAt - a.createdAt));
            } else {
                setTestimonials([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [productId]);

    if (loading) return (
        <div className="py-10 flex justify-center">
            <div className="animate-pulse flex space-x-4">
                <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                <div className="flex-1 space-y-6 py-1">
                    <div className="h-2 bg-gray-200 rounded"></div>
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="h-2 bg-gray-200 rounded col-span-2"></div>
                            <div className="h-2 bg-gray-200 rounded col-span-1"></div>
                        </div>
                        <div className="h-2 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (testimonials.length === 0) {
        if (isCreator) {
            return (
                <div className="mt-12 mb-8 p-8 rounded-3xl border-2 border-dashed border-gray-200 dark:border-white/10 flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-400 mb-3">
                        <FiMessageSquare className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg dark:text-white">No testimonials yet</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                        Use the "Manage Testimonials" menu to add feedback or approve submissions.
                    </p>
                </div>
            );
        }
        return null;
    }

    return (
        <div className="mt-12 mb-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">What people are saying</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Real results from our creative community</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((item) => (
                    <div 
                        key={item.id}
                        className="group relative bg-white dark:bg-[#1E1E1E]/50 backdrop-blur-xl border border-gray-100 dark:border-white/5 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
                    >
                        {/* Quote mark accent */}
                        <div className="absolute top-4 right-6 text-6xl text-[#327691]/10 font-serif pointer-events-none">&ldquo;</div>
                        
                        <div className="flex flex-col h-full">
                            {/* Author */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-[#327691]/20 flex items-center justify-center text-[#327691] font-bold text-lg">
                                    {item.name.charAt(0)}
                                </div>
                                <div>
                                    <h4 className="font-bold text-sm dark:text-white">{item.name}</h4>
                                    <div className="flex text-yellow-400 text-[10px]">
                                        {Array.from({ length: item.rating || 5 }).map((_, i) => (
                                            <span key={i}>★</span>
                                        ))}
                                        {Array.from({ length: 5 - (item.rating || 5) }).map((_, i) => (
                                            <span key={i} className="text-gray-300">★</span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Feedback Text */}
                            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4 italic flex-1">
                                "{item.text}"
                            </p>

                            {/* Media Attachments */}
                            <div className="mt-auto space-y-3">
                                {item.imageUrl && (
                                    <div className="relative rounded-2xl overflow-hidden aspect-video border border-gray-100 dark:border-white/10">
                                        <img 
                                            src={item.imageUrl} 
                                            alt="Testimonial screenshot" 
                                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
                                            onClick={() => window.open(item.imageUrl, '_blank')}
                                        />
                                    </div>
                                )}

                                {item.videoUrl && (
                                    <button 
                                        onClick={() => setSelectedVideo(item.videoUrl)}
                                        className="w-full py-2.5 rounded-xl bg-[#327691]/10 text-[#327691] text-xs font-bold flex items-center justify-center gap-2 hover:bg-[#327691] hover:text-white transition-all border border-[#327691]/20"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M8 5v14l11-7z"/>
                                        </svg>
                                        Watch Video Feedback
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Video Modal */}
            {selectedVideo && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="relative w-full max-w-4xl aspect-video rounded-3xl overflow-hidden bg-black shadow-2xl">
                        <button 
                            onClick={() => setSelectedVideo(null)}
                            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                        
                        {selectedVideo.includes('iframe.mediadelivery.net') ? (
                            <BunnyVideoEmbed 
                                videoUrl={selectedVideo}
                                variant="modal"
                                autoplay={true}
                            />
                        ) : (
                            <iframe 
                                src={selectedVideo}
                                className="w-full h-full border-0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                                title="Testimonial Video"
                            ></iframe>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Testimonials;
